import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { Avatar } from '@/components'
import { AdaptedGoalTransferDTO } from '@/domain/transfer'
import { useTheme } from '@/hooks/useTheme'
import { useGetWallet } from '@/domain/wallet'

interface SavingsTransactionItemProps {
  transaction: AdaptedGoalTransferDTO
  isLastActivities?: boolean
  onPress?: () => void
}

export const SavingsTransactionItem: React.FC<SavingsTransactionItemProps> = ({
  transaction,
  isLastActivities = false,
  onPress,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()

  // Get wallet data for currency info (like in React Native)
  const { data: walletData } = useGetWallet()

  // Find USDT currency data
  const usdtCurrency = walletData?.completeCurrencies?.find(
    (currency) => currency.code === transaction.amount.currency,
  )

  const handlePress = () => {
    if (onPress) {
      onPress()
    } else {
      // Navigate to transaction details (similar to RN version)
      navigate(
        `/savings/transfer-details/${transaction.saving_goal_id}/${transaction.id}`,
      )
    }
  }

  // Determine if this is a transfer out (for styling)
  const isTransferOut = transaction.type === 'transfer_out'

  // Determine if we should render currency icon (no displayIconName means use currency icon)
  const shouldRenderCurrencyIcon = !transaction.displayIconName

  // Get adapted icon background color (like in React Native)
  const adaptedIconBgColor =
    transaction.displayIconBgColor || usdtCurrency?.avatarBGRefColor

  // Get amount color based on transaction type
  const getAmountColor = () => {
    if (transaction.type === 'earning') {
      return theme.colors.label.success || '#34C759'
    }
    return !isTransferOut
      ? theme.colors.label.success || '#34C759'
      : theme.colors.label.default
  }

  // Get amount prefix
  const getAmountPrefix = () => {
    if (transaction.type === 'earning') {
      return '+'
    }
    return !isTransferOut ? '+' : '-'
  }

  return (
    <CardItem.Root
      onPress={handlePress}
      style={{
        padding: '11px 12px',
        // paddingTop: 12,
        // paddingBottom: 12,
        backgroundColor: theme.colors.background.cardItemRoot,
        borderRadius: 28,
        marginBottom: 8,
      }}
    >
      <Avatar
        isCurrency={shouldRenderCurrencyIcon}
        iconName={
          !shouldRenderCurrencyIcon ? transaction.displayIconName : undefined
        }
        uri={shouldRenderCurrencyIcon ? usdtCurrency?.icon : undefined}
        size={48}
        iconSize={transaction.displayIconSize || 24}
        bgColor={adaptedIconBgColor}
        containerStyle={{
          backgroundColor: adaptedIconBgColor,
        }}
      />

      <CardItem.Content
        style={{
          gap: 6,
          flex: 1,
        }}
      >
        <CardItem.Title
          title={t(transaction.displayTitle)}
          style={{
            fontSize: 18,
          }}
        />
        <BaseText
          fontFamily="semibold"
          color="label.description"
          style={{
            fontSize: 16,
          }}
        >
          {isLastActivities
            ? transaction.formattedDate
            : transaction.formattedTime}
        </BaseText>
      </CardItem.Content>

      <CardItem.Footer style={{ gap: 6 }}>
        <BaseText
          fontFamily="medium"
          style={{
            color: getAmountColor(),
            fontSize: 18,
          }}
        >{`${getAmountPrefix()}${transaction.formattedCryptoAmount}`}</BaseText>
        <BaseText color="label.description" fontSize={16} fontFamily="medium">
          ≈{transaction.formattedFiatAmount}
        </BaseText>
      </CardItem.Footer>
    </CardItem.Root>
  )
}
